import styles from './DeathMessage.module.css';
import { useEffect, useState, useRef } from 'react';

interface DeathMessageProps {
  icon: string;
  displayName: string;
  isPersona?: boolean;
}

function DeathMessage({ icon, displayName, isPersona = false }: DeathMessageProps): JSX.Element {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVisible, setIsVisible] = useState(false);
  const [showBloodSplatters, setShowBloodSplatters] = useState(false);
  
  // Use refs to store timeouts for proper cleanup
  const messageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const splatterTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation effects sequence
  useEffect(() => {
    // Initial delay before showing message
    messageTimerRef.current = setTimeout(() => {
      setIsVisible(true);
      
      // Show blood splatters after a short delay
      splatterTimerRef.current = setTimeout(() => {
        setShowBloodSplatters(true);
      }, 200);
      
    }, 300);
    
    // Cleanup function to clear all timers
    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }
      if (splatterTimerRef.current) {
        clearTimeout(splatterTimerRef.current);
      }
    };
  }, [isPersona, displayName]);

  // Determine the appropriate CSS classes based on message type and state
  const containerClass = `${styles.deathMessage} ${isVisible ? styles.visible : ''}`;
  
  return (
    <div className={containerClass}>
      {showBloodSplatters && (
        <div className={styles.bloodOverlay}>
          <div className={`${styles.bloodSplatter} ${styles.splatterTop}`}></div>
          <div className={`${styles.bloodSplatter} ${styles.splatterLeft}`}></div>
          <div className={`${styles.bloodSplatter} ${styles.splatterRight}`}></div>
        </div>
      )}
      
      <div className={styles.narratorContent}>
        <div className={styles.deathMessageIconContainer}>
          <img src={icon} alt={displayName} className={styles.deathMessageIcon} />
          <div className={styles.iconOverlay}></div>
        </div>
        
        <div className={styles.deathMessageContent}>
          <div className={styles.deathMessageText}>
            {isPersona ? 'You have died' : `${displayName} has died`}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeathMessage; 